import { useState, useEffect } from 'react';
import { FaPlus, FaTimes, FaSync, FaEllipsisV } from 'react-icons/fa';
import { Menu, MenuItem } from '@mui/material';
import axios from '../../services/api';
import { toast } from 'react-toastify';
import CreateLockModal from './modals/CreateLockModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

export default function LockManagement() {
  const { t } = useTranslation();
  const [locks, setLocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLock, setSelectedLock] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [lockToDelete, setLockToDelete] = useState(null);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    fetchLocks();
  }, []);

  const fetchLocks = async () => {
    try {
      const response = await axios.get('/locks');
      setLocks(response.data);
      setTotalQuantity(response.data.length);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteClick = lock => {
    setLockToDelete(lock);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/locks/${lockToDelete._id}`);
      toast.success(t('Lock deleted successfully'));
      fetchLocks();
      setIsConfirmModalOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedLock(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchLocks();
  };
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLocks();
    setIsRefreshing(false);
  };
  const handleMenuOpen = (event, lock) => {
    setAnchorEl(event.currentTarget);
    setSelectedLock(lock);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLock(null);
  };

  const filteredLocks = locks
    .filter(lock => {
      const matchesSearch =
        lock.uuid.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lock.address || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        filterStatus === 'all'
          ? true
          : filterStatus === 'installed'
            ? lock.installDate
            : filterStatus === 'uninstalled'
              ? !lock.installDate
              : true;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const nameA = (
        a?.buildingInfo?.buildingName ||
        a?.entranceInfo?.entranceName ||
        '-'
      ).toLowerCase();
      const nameB = (
        b?.buildingInfo?.buildingName ||
        b?.entranceInfo?.entranceName ||
        '-'
      ).toLowerCase();

      if (nameA === '-' && nameB !== '-') return 1;
      if (nameA !== '-' && nameB === '-') return -1;

      return nameA.localeCompare(nameB);
    });

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      <div className="flex justify-between items-center bg-skin-primary p-4 rounded-xl shadow-lg h-filter">
        <div className="flex items-center gap-4 w-full pr-4">
          <select
            className="bg-white border border-gray-300 rounded-lg px-3 py-2"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="all">{t('All')}</option>
            <option value="installed">{t('Installed')}</option>
            <option value="uninstalled">{t('Uninstalled')}</option>
          </select>

          <div className="relative" style={{ width: '100%' }}>
            <input
              type="text"
              placeholder={t('Search')}
              className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors w-full"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="bg-skin-base hover:bg-skin-hover px-2 py-2 rounded-lg flex items-center gap-2
                    text-skin-base transition-colors disabled:opacity-50"
          >
            <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 btn-min-width"
        >
          <FaPlus /> {t('Create')}
        </button>
      </div>

      <div className="h-title flex items-center">
        <span className="text-gray-600">
          {t('Number of Door Lock')}: {totalQuantity}
        </span>
      </div>

      <div className="relative overflow-x-auto overflow-y-auto h-table-height-title">
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[50px]">
                {t('No.')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('UUID')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Relay ID')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Door ID')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Door Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base">
                {t('Install Date')}
              </th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-skin-base">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLocks.map((lock, index) => (
              <tr key={lock._id} className="border-b">
                <td className="px-6 py-4">{index + 1}</td>
                <td className="px-6 py-4">{lock.uuid}</td>
                <td className="px-6 py-4">{lock.relayId}</td>
                <td className="px-6 py-4">
                  {lock?.buildingInfo?.buildingId || lock?.entranceInfo?.entranceId || '-'}
                </td>
                <td className="px-6 py-4">
                  {lock?.buildingInfo?.buildingName || lock?.entranceInfo?.entranceName || '-'}
                </td>
                <td className="px-6 py-4">
                  {/* {lock.installDate ? new Date(lock.installDate).toLocaleDateString() : '-'} */}
                  {lock?.buildingInfo?.assignedAt
                    ? new Date(lock?.buildingInfo?.assignedAt).toLocaleDateString()
                    : lock?.entranceInfo?.assignedAt
                      ? new Date(lock?.entranceInfo?.assignedAt).toLocaleDateString()
                      : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={e => handleMenuOpen(e, lock)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <FaEllipsisV className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateLockModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        lock={selectedLock}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('Delete Lock')}
        message={t('Are you sure you want to delete the lock "{{uuid}}"?', {
          uuid: lockToDelete?.uuid,
        })}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            setSelectedLock(selectedLock);
            setIsModalOpen(true);
          }}
        >
          {t('Edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleDeleteClick(selectedLock);
          }}
        >
          {t('Delete')}
        </MenuItem>
      </Menu>
    </div>
  );
}
